import { useState } from 'react'

// import clsx from 'clsx'
// import { PreventFlashOnWrongTheme, ThemeProvider } from 'remix-themes'

import { NuqsAdapter } from 'nuqs/adapters/react-router/v7'
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Navigate,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData
} from 'react-router'

import type { LinksFunction, LoaderFunctionArgs } from 'react-router'

import {
  HydrationBoundary,
  QueryClient,
  QueryClientProvider
} from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'

import { useDehydratedState } from './hooks/use-dehydrated-state' // updated to use new Remix
// import Analytics from '~/components/Analytics/Analytics' // May need to revisit if too many tags are slowing down the site

// import DelayedTagsTrigger from './components/Analytics/DelayedTrigger'

// import { themeSessionResolver } from './sessions.server'

import { checkUrl } from './lib/contensis/versionSwitch'
import { initClient } from './lib/contensis/client'

import { getLastUrlSegment } from './utils/misc'
// import { ErrorPage } from './components/ErrorPage'

import type { Route } from './+types/root'

import './tailwind.css'

export async function loader({ request }: LoaderFunctionArgs) {
  const currentUrl = new URL(request.url)
  const versCookie = request.headers.get('x-entry-versionstatus') || null
  const versionStatus = checkUrl(currentUrl.hostname, versCookie)
  initClient(versionStatus)

  const lastPath = getLastUrlSegment(currentUrl.pathname)

  // console.log('Entry ID from request: ', currentUrl.searchParams.get('entryId'))
  // console.log('NODE ID from request: ', currentUrl.searchParams.get('nodeId'))

  // const { getTheme } = await themeSessionResolver(request)

  return {
    lastPath,
    versCookie,
    versionStatus,
    currentUrl
    // theme: getTheme()
  }
}

export const links: LinksFunction = () => [
  {
    rel: 'icon',
    type: 'image/svg+xml',
    href: '/site-elements/images/favicon/favicon.svg'
  },
  {
    rel: 'icon',
    type: 'image/png',
    href: '/site-elements/images/favicon/favicon.png'
  },
  {
    rel: 'apple-touch-icon',
    sizes: '180x180',
    href: '/site-elements/images/favicon/apple-touch-icon.png'
  },
  {
    rel: 'manifest',
    href: '/site-elements/images/favicon/site.json'
  },
  {
    rel: 'mask-icon',
    href: '/site-elements/images/favicon/safari-pinned-tab.svg',
    color: '#0e1647'
  },
  {
    rel: 'shortcut icon',
    href: '/site-elements/images/favicon/favicon.ico'
  },
  {
    rel: 'preconnect',
    href: 'https://cms-uni-hull.cloud.contensis.com'
  },
  {
    rel: 'preload',
    href: '/site-elements/fonts/signifier/signifier-medium.woff2',
    as: 'font',
    type: 'font/woff2',
    crossOrigin: 'anonymous'
  },
  {
    rel: 'preload',
    href: '/site-elements/fonts/signifier/signifier-medium-italic.woff2',
    as: 'font',
    type: 'font/woff2',
    crossOrigin: 'anonymous'
  },
  {
    rel: 'preload',
    href: '/site-elements/fonts/signifier/signifier-regular.woff2',
    as: 'font',
    type: 'font/woff2',
    crossOrigin: 'anonymous'
  },
  {
    rel: 'preload',
    href: '/site-elements/fonts/signifier/signifier-regular-italic.woff2',
    as: 'font',
    type: 'font/woff2',
    crossOrigin: 'anonymous'
  },
  {
    rel: 'preload',
    href: '/site-elements/fonts/suisse/suisse-intl-book.woff2',
    as: 'font',
    type: 'font/woff2',
    crossOrigin: 'anonymous'
  },
  {
    rel: 'preload',
    href: '/site-elements/fonts/suisse/suisse-intl-book-italic.woff2',
    as: 'font',
    type: 'font/woff2',
    crossOrigin: 'anonymous'
  },
  {
    rel: 'preload',
    href: '/site-elements/fonts/suisse/suisse-intl-bold.woff2',
    as: 'font',
    type: 'font/woff2',
    crossOrigin: 'anonymous'
  },
  {
    rel: 'preload',
    href: '/site-elements/fonts/suisse/suisse-intl-bold-italic.woff2',
    as: 'font',
    type: 'font/woff2',
    crossOrigin: 'anonymous'
  },
  {
    rel: 'preload',
    href: '/site-elements/fonts/suisse/suisse-intl-mono-regular.woff2',
    as: 'font',
    type: 'font/woff2',
    crossOrigin: 'anonymous'
  },
  {
    rel: 'preload',
    href: '/site-elements/fonts/suisse/suisse-intl-mono-bold.woff2',
    as: 'font',
    type: 'font/woff2',
    crossOrigin: 'anonymous'
  }
]

// export default function AppWithProviders() {
//   // const data = useLoaderData<typeof loader>()
//   return (
//     // <ThemeProvider specifiedTheme={data.theme} themeAction='/action/set-theme'>
//     <App />
//     // </ThemeProvider>
//   )
// }

export function Layout({ children }: { children: React.ReactNode }) {
  const data = useLoaderData<typeof loader>()
  // const [theme] = useTheme()

  const currentUrl = data.currentUrl.hostname

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // With SSR, we usually want to set some default staleTime
            // above 0 to avoid refetching immediately on the client
            staleTime: data.versionStatus === 'published' ? 60 * 1000 : 0
          }
        }
      })
  )

  const dehydratedState = useDehydratedState()

  // if (
  //   process.env.NODE_ENV !== 'development' &&
  //   (!currentUrl.startsWith('staging-') || !currentUrl.startsWith('preview-'))
  // ) {
  //   Analytics()
  // }

  return (
    // <html lang='en' className={clsx(theme)}>
    <html lang='en'>
      <head>
        <meta charSet='utf-8' />
        <meta name='viewport' content='width=device-width, initial-scale=1' />

        <Meta />
        {/* <PreventFlashOnWrongTheme ssrTheme={Boolean(data.theme)} /> */}
        <Links />

        <meta name='msapplication-TileColor' content='#ffffff' />
        <meta
          name='msapplication-config'
          content='/site-elements/image/favicon/browserconfig.xml'
        />

        {process.env.NODE_ENV !== 'development' &&
          (!currentUrl.startsWith('staging-') ||
            !currentUrl.startsWith('preview-')) && (
            <>
              <script
                dangerouslySetInnerHTML={{
                  __html: `
              (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
              })(window,document,'script','dataLayer','GTM-5GXGL2LZ');
            `
                }}
              />
              {/* <DelayedTagsTrigger /> */}
            </>
          )}
      </head>
      <body suppressHydrationWarning={true} className='overflow-x-hidden'>
        {process.env.NODE_ENV !== 'development' &&
          (!currentUrl.startsWith('staging-') ||
            !currentUrl.startsWith('preview-')) && (
            <>
              <noscript>
                <iframe
                  src='https://www.googletagmanager.com/ns.html?id=GTM-5GXGL2LZ'
                  title='Google Tag Manager'
                  height='0'
                  width='0'
                  style={{ display: 'none', visibility: 'hidden' }}
                ></iframe>
              </noscript>
            </>
          )}
        <NuqsAdapter>
          <QueryClientProvider client={queryClient}>
            <HydrationBoundary state={dehydratedState}>
              {children}
            </HydrationBoundary>

            <ReactQueryDevtools initialIsOpen={false} />
          </QueryClientProvider>
        </NuqsAdapter>

        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  )
}

export default function App() {
  return <Outlet />
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let message = 'Oops!'
  let details = 'An unexpected error occurred.'
  let stack: string | undefined

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? '404' : 'Error'
    details =
      error.status === 404
        ? 'The requested page could not be found.'
        : error.statusText || details
  } else if (import.meta.env.DEV && error && error instanceof Error) {
    details = error.message
    stack = error.stack
  }

  if (isRouteErrorResponse(error) && error.status === 404) {
    return <Navigate to='/notfound' replace />
  }

  return (
    <main className='space-y-m p-s pt-xl container mx-auto'>
      <h1 className='font-serif font-bold'>{message}</h1>
      <p className='text-[#EE0000]'>{details}</p>
      {stack && (
        <pre className='py-s px-m w-full overflow-x-auto rounded-lg bg-[#FAEBD7] font-mono text-[#EE0000] shadow-xl/30'>
          <code>{stack}</code>
        </pre>
      )}
    </main>
  )
}
